import angular from 'angular'

import browser from '../utils/browser'
import isWebSafeFont from '../utils/is-web-safe-font'
import googleFontLink from '../utils/google-font-link'

import { negate } from 'lodash-es'
import { toRgb, isText, toPixel, toPosPixel, toPosNumber } from '../utils/bits'

import { MOBILE_THRESHOLD } from '../utils/shared-constants'

angular
  .module('ggs.gallery')
  .factory('GalleryStyleHandlers', GalleryStyleHandlersProvider)

function GalleryStyleHandlersProvider(
  env,
  domh,
  rootQs,
  htmlOverflowY,
  GSNApplyLinkTmpl,
  GSNApplyStyleTmpl,
  cssElementQueries) {

  const OWNER_TAG = __filename

  return function GalleryStyleHandlers(extFlow) {

    // applyBodyScrollbar

    const BODY_SCROLLBAR_PLH = ':overflow-y:'
    const BODY_SCROLLBAR_TMPL = `
      /* allowed to skip the rootQs */
      body {
        overflow-y: ${BODY_SCROLLBAR_PLH};
      }`

    const applyBodyScrollbar = GSNApplyStyleTmpl({
      propKeys: [
        'carousel_mode',
        'carousel_layout_classic',
        'body_background_show',
        'body_background_image',
        'body_bg_image_stretch',
        'allow_overflow_y_change'
      ],
      ownerTag: OWNER_TAG,
      styleFunc: (carousel_mode, carousel_layout_classic, _body_background_show, _body_background_image, _body_bg_image_stretch, _allow_overflow_y_change) => {
        let overflowY = null
        if (env.isHostedMode) {
          overflowY = (carousel_layout_classic || !carousel_mode) ? 'scroll' : 'auto'
        } else if (env.isIframeMode) {
          overflowY = 'hidden'
        }

        if (overflowY) {
          return BODY_SCROLLBAR_TMPL.replace(BODY_SCROLLBAR_PLH, overflowY)
        }
      },
      afterFunc: (_carousel_mode, _carousel_layout_classic, body_background_show, body_background_image, body_bg_image_stretch, allow_overflow_y_change) => {
        const stretch = true
          && body_background_show
          && body_bg_image_stretch
          && isText(body_background_image)

        const inject = true
          && !stretch
          && !env.isIframeMode
          && allow_overflow_y_change

        return inject
          ? htmlOverflowY.attachStyles()
          : htmlOverflowY.detachStyles()
      },
      destroy: htmlOverflowY.detachStyles
    })

    // applyBodyBackgroundColor

    const BODY_BG_COLOR_PLH = ':background-color:'
    const BODY_BG_COLOR_OPACITY_PLH = ':background-opacity:'
    const BODY_BG_COLOR_SELECTOR_PLH = ':background-selector:'

    const BODY_BG_COLOR_TMPL = `
      /* allowed to skip the rootQs */
      ${BODY_BG_COLOR_SELECTOR_PLH} {
        background: rgba(${BODY_BG_COLOR_PLH}, ${BODY_BG_COLOR_OPACITY_PLH});
      }`

    const applyBodyBackgroundColor = GSNApplyStyleTmpl({
      propKeys: [
        'body_bg_color_opacity',
        'body_background_color',
        'body_bg_apply_to_root'
      ],
      ownerTag: OWNER_TAG,
      allowEmpty: false,
      allowFunc: (_body_bg_color_opacity, _body_background_color, _body_bg_apply_to_root) => {
        return !env.isIframeMode
      },
      styleFunc: (body_bg_color_opacity, body_background_color, body_bg_apply_to_root) => {
        const color = toRgb(body_background_color)
        const opacity = body_bg_color_opacity
        const selector = body_bg_apply_to_root
          ? env.isHostedMode
            ? 'html'
            : rootQs
          : `${rootQs} .ggs-gallery__body`

        return BODY_BG_COLOR_TMPL
          .replace(BODY_BG_COLOR_PLH, color)
          .replace(BODY_BG_COLOR_OPACITY_PLH, opacity)
          .replace(BODY_BG_COLOR_SELECTOR_PLH, selector)
      }
    })

    // applyBodyBackgroundImage

    const BODY_BG_IMAGE_PLH = ':background-image:'
    const BODY_BG_IMAGE_SELECTOR_PLH = ':background-selector:'

    const BODY_BG_COMMON_IMAGE_TMPL = `
      /* allowed to skip the rootQs */
      ${BODY_BG_IMAGE_SELECTOR_PLH} {
        background-image: url("${BODY_BG_IMAGE_PLH}");
        background-size: unset;
        background-repeat: repeat;
        background-position: top center;
        background-attachment: scroll;
      }`
    const BODY_BG_COMMON_IMAGE_STRETCH_TMPL = `
      /* allowed to skip the rootQs */
      ${BODY_BG_IMAGE_SELECTOR_PLH} {
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
        background-attachment: fixed;
      }`

    const applyBodyBackgroundImage = GSNApplyStyleTmpl({
      propKeys: [
        'body_bg_image_stretch',
        'body_background_image',
        'body_bg_apply_to_root'
      ],
      ownerTag: OWNER_TAG,
      allowEmpty: false,
      allowFunc: (body_bg_image_stretch, _body_background_image, _body_bg_apply_to_root) => {
        if (env.isIframeMode) return false
        if (body_bg_image_stretch && browser.likeIE) return false
        return true
      },
      styleFunc: (body_bg_image_stretch, body_background_image, body_bg_apply_to_root) => {
        const tmpl = body_bg_image_stretch
          ? BODY_BG_COMMON_IMAGE_TMPL + BODY_BG_COMMON_IMAGE_STRETCH_TMPL
          : BODY_BG_COMMON_IMAGE_TMPL

        const selector = body_bg_apply_to_root
          ? env.isHostedMode
            ? 'html'
            : rootQs
          : `${rootQs} .ggs-gallery__body`

        return tmpl
          .replace(BODY_BG_IMAGE_PLH, body_background_image)
          .replace(RegExp(BODY_BG_IMAGE_SELECTOR_PLH, 'g'), selector)
      }
    })

    // applyCustomStyles

    const applyCustomStyles = GSNApplyStyleTmpl({
      propKeys: 'custom_styles',
      ownerTag: OWNER_TAG,
      allowEmpty: false,
      allowFunc: (custom_styles) => domh.checkCssBrackets(custom_styles),
      styleFunc: (custom_styles) => domh.addRootQs(custom_styles)
    })

    // applyBodyFontName

    const BODY_FONT_NAME_PLH = ':font-family:'
    const BODY_FONT_NAME_TMPL = `
      ${rootQs} {
        font-family: '${BODY_FONT_NAME_PLH}', 'Lato', sans-serif;
      }`

    const applyBodyFontName = GSNApplyStyleTmpl({
      propKeys: 'body_font_name',
      ownerTag: OWNER_TAG,
      styleTmpl: BODY_FONT_NAME_TMPL,
      stylePlh: BODY_FONT_NAME_PLH
    })

    // applyBodyFontLink

    const applyBodyFontLink = GSNApplyLinkTmpl({
      propKeys: 'body_font_name',
      ownerTag: OWNER_TAG,
      linkFunc: googleFontLink,
      allowEmpty: false,
      allowFunc: negate(isWebSafeFont)
    })

    //   applyBodyPaddings

    const applyBodyPadding = GSNApplyStyleTmpl({
      propKeys: [
        'post_space',
        'body_side_margin',
        'allow_entry',
        'allow_sorting'
      ],
      ownerTag: OWNER_TAG,
      allowEmpty: false,
      styleFunc: buildBodyPaddings,
      afterFunc: afterBodyPaddings
    })

    const BP_MARGIN_TOP_PLH = ':margin-top:'
    const BP_PADDING_TOP_PLH = ':padding-top:'
    const BP_PADDING_LEFT_PLH = ':padding-left:'
    const BP_PADDING_RIGHT_PLH = ':padding-right:'
    const BP_PADDING_BOTTOM_PLH = ':padding-bottom:'
    const BP_TMPL = `
      ${rootQs} .ggs-gallery__content {
        margin-top:     ${BP_MARGIN_TOP_PLH};
        padding-top:    ${BP_PADDING_TOP_PLH};
        padding-left:   ${BP_PADDING_LEFT_PLH};
        padding-right:  ${BP_PADDING_RIGHT_PLH};
        padding-bottom: ${BP_PADDING_BOTTOM_PLH};
      }

      ${rootQs} .ggs-gallery__buttons-line {
        padding-left:  ${BP_PADDING_LEFT_PLH};
        padding-right: ${BP_PADDING_RIGHT_PLH};
      }

      ${rootQs}[max-width~="${MOBILE_THRESHOLD}"] .ggs-gallery__buttons-line {
        padding-left:  0px;
        padding-right: 0px;
      }`

    function buildBodyPaddings(
      post_space,
      body_side_margin,
      allow_entry,
      allow_sorting) {

      const hasButtonsLine = allow_entry || allow_sorting

      const marginTop = hasButtonsLine
        ? toPixel(-post_space / 2)
        : toPixel(0)
      const paddingTop = hasButtonsLine
        ? toPixel(0)
        : toPosPixel(body_side_margin)
      const paddingSide = toPosPixel(body_side_margin)
      const paddingBottom = toPosPixel(body_side_margin)

      return BP_TMPL
        .replace(RegExp(BP_MARGIN_TOP_PLH, 'g'), marginTop)
        .replace(RegExp(BP_PADDING_TOP_PLH, 'g'), paddingTop)
        .replace(RegExp(BP_PADDING_LEFT_PLH, 'g'), paddingSide)
        .replace(RegExp(BP_PADDING_RIGHT_PLH, 'g'), paddingSide)
        .replace(RegExp(BP_PADDING_BOTTOM_PLH, 'g'), paddingBottom)
    }

    function afterBodyPaddings() {
      cssElementQueries.update()
      return extFlow.resize()
    }

    // applyLoadMoreMargin

    const applyLoadMoreMargin = GSNApplyStyleTmpl({
      propKeys: [
        'post_space',
        'body_side_margin'
      ],
      ownerTag: OWNER_TAG,
      allowEmpty: false,
      styleFunc: buildLoadMoreMargin
    })

    const LMM_TOP_PLH = ':margin-top:'
    const LMM_BTM_PLH = ':padding-bottom:'
    const LMM_TMPL = `
      ${rootQs} .ggs-gallery__load-more-button {
        margin-top:     ${LMM_TOP_PLH};
        padding-bottom: ${LMM_BTM_PLH};
        /* padding-bottom instead of margin-bottom
           to fix gallery body height in firefox: http://imgur.com/9r1U2f8
        */
      }`

    const LM_HEIGHT = 54
    const LM_MIN_MARGIN = 20

    function buildLoadMoreMargin(post_space, body_side_margin) {
      //   ...
      //    | preview flow padding
      //   ---
      //    | gallery bottom padding
      //   ---
      //    | load more button
      //  [/end]

      const galleryBodyBottomPadding = toPosNumber(body_side_margin)
      const previewFlowBottomPadding = toPosNumber(post_space / 2)
      const sumPadding = galleryBodyBottomPadding + previewFlowBottomPadding

      const naiveOffset = Math.round((sumPadding - LM_HEIGHT) / 2)
      const goalOffset = Math.max(LM_MIN_MARGIN, naiveOffset)

      const topMargin = toPixel(goalOffset - sumPadding) // can be negative
      const btmMargin = toPosPixel(goalOffset)

      return LMM_TMPL
        .replace(LMM_TOP_PLH, topMargin)
        .replace(LMM_BTM_PLH, btmMargin)
    }

    // applyPreviewCursor

    const PREVIEW_CURSOR_PLH = ':cursor:'
    const PREVIEW_CURSOR_TMPL = `
      ${rootQs} .ggs-gallery-preview__showcase {
        cursor: ${PREVIEW_CURSOR_PLH};
      }`

    const applyPreviewCursor = GSNApplyStyleTmpl({
      propKeys: 'modals_use',
      ownerTag: OWNER_TAG,
      styleFunc: (modals_use) => {
        const cursor = modals_use
          ? 'pointer'
          : 'default'
        return PREVIEW_CURSOR_TMPL.replace(PREVIEW_CURSOR_PLH, cursor)
      }
    })

    return [
      applyBodyScrollbar,
      applyBodyBackgroundColor,
      applyBodyBackgroundImage,
      applyCustomStyles,
      applyBodyFontName,
      applyBodyFontLink,
      applyBodyPadding,
      applyLoadMoreMargin,
      applyPreviewCursor
    ]
  }
}
